import {
	formatCurrencyPrecise,
	formatNumber,
	getActionTypeLabel,
} from "@dune/utils";
import {
	ACTION_BUILDING_TYPES,
	actionBuildingTypeCheckboxes,
} from "@dune/vars";
import { format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { ConstructionSiteDetailsFormData } from "./useConstructionSiteDetailsForm";

interface DetailsDescriptionContainerProps {
	showOperationDetails?: boolean;
}

const DetailsDescriptionContainer = ({
	showOperationDetails = false,
}: DetailsDescriptionContainerProps) => {
	const { watch } = useFormContext<ConstructionSiteDetailsFormData>();

	const {
		rga,
		sismic,
		streetNumber,
		streetName,
		postalCode,
		city,
		actions,
		startDate,
		deliveryDate,
		cityHallStartDate,
		totalAmountTTC,
		totalAmountHT,
		taxMode,
	} = watch();
	const formBuildingDetailsValues: Record<string, string> | undefined =
		actions?.buildingType
			? actions?.buildingDetails?.[actions.buildingType]
			: undefined;

	const selectedBuildingTypeData = actionBuildingTypeCheckboxes.find(
		(checkbox) => checkbox.value === actions?.buildingType,
	);

	const containerClass = showOperationDetails
		? "flex flex-col gap-4 mb-6"
		: "flex flex-1 flex-col self-start gap-4 p-4 sticky top-10";

	const hasValidValue = (fieldName: string): boolean => {
		return !!(
			formBuildingDetailsValues?.[fieldName] &&
			formBuildingDetailsValues[fieldName] !== "" &&
			formBuildingDetailsValues[fieldName] !== "0"
		);
	};

	const hasExtension = hasValidValue("extensionSurface");

	const commonParts =
		actions?.types?.includes("SYNDIC") &&
		actions?.buildingType === "COMMON_PARTS";
	const privateWork =
		actions?.types?.includes("SYNDIC") &&
		actions?.buildingType === "PRIVATE_WORK";
	return (
		<div className={containerClass}>
			<div className="flex flex-col gap-4">
				<h1 className="text-[#144C65] text-md font-medium">
					Descriptif de l'opération
				</h1>
				<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4 bg-gray-100">
					{streetNumber && streetName && postalCode && city && (
						<p>
							L'opération à assurer se situe au{" "}
							<b>{`${streetNumber} ${streetName}, ${postalCode} ${city}.`}</b>
						</p>
					)}

					{actions?.types &&
						actions?.types.length > 0 &&
						actions?.buildingType && (
							<>
								<p>
									{!commonParts && !privateWork ? (
										<>
											Elle consiste en{" "}
											<b>
												{actions?.types
													?.map((type) => getActionTypeLabel(type))
													?.join(" et ")}{" "}
												{actions?.buildingType &&
													ACTION_BUILDING_TYPES[actions.buildingType]}
											</b>
										</>
									) : (
										<>
											Elle concerne{" "}
											<b>
												{privateWork
													? "des travaux privatifs"
													: "des parties communes"}
											</b>
										</>
									)}
									{selectedBuildingTypeData?.details &&
										formBuildingDetailsValues &&
										selectedBuildingTypeData.details
											.filter(
												(detail) =>
													detail.values.some((value) =>
														hasValidValue(value.name),
													) &&
													!detail.values.some(
														(val) =>
															val.name === "surface" ||
															val.name === "extensionSurface",
													),
											)
											.map((buildingDetail, detailIndex) => {
												const validValues = buildingDetail.values.filter(
													(value) => hasValidValue(value.name),
												);

												if (validValues.length === 0) return null;

												if (buildingDetail.values.length > 1) {
													return (
														<span key={buildingDetail.values[0].name}>
															{" "}
															{buildingDetail.label.toLowerCase()}{" "}
															{validValues.map((value, index) => {
																const fieldValue =
																	formBuildingDetailsValues[value.name];
																return (
																	<span key={value.name}>
																		{index > 0 &&
																			` ${buildingDetail.separator} `}
																		<b>
																			{fieldValue &&
																			!Number.isNaN(Number(fieldValue))
																				? formatNumber(fieldValue.toString())
																				: fieldValue}
																			{value.indication &&
																				` ${value.indication}`}
																		</b>
																	</span>
																);
															})}
														</span>
													);
												}

												const fieldValue =
													formBuildingDetailsValues[
														buildingDetail.values[0].name
													];
												return (
													<span key={buildingDetail.values[0].name}>
														{detailIndex === 0 ? " " : " et "}
														{buildingDetail.label.toLowerCase()}{" "}
														<b>
															{fieldValue && !Number.isNaN(Number(fieldValue))
																? formatNumber(fieldValue.toString())
																: fieldValue}{" "}
															{buildingDetail.values[0].indication}
														</b>
													</span>
												);
											})}
									.
								</p>
								{(hasValidValue("surface") ||
									totalAmountHT ||
									totalAmountTTC) && (
									<p>
										{hasExtension ? (
											<span>
												L'extension créée{" "}
												<b>
													{formBuildingDetailsValues?.extensionSurface
														? formatNumber(
																formBuildingDetailsValues.extensionSurface,
															)
														: 0}{" "}
													m²
												</b>
												{" pour une surface totale du bâtiment de "}
												<b>
													{formBuildingDetailsValues?.surface &&
														formatNumber(
															formBuildingDetailsValues.surface,
														)}{" "}
													m²
												</b>
											</span>
										) : hasValidValue("surface") ? (
											<span>
												La surface totale du bâtiment est de{" "}
												<b>
													{formBuildingDetailsValues?.surface &&
														formatNumber(
															formBuildingDetailsValues.surface,
														)}{" "}
													m²
												</b>
											</span>
										) : null}
										{(totalAmountHT || totalAmountTTC) && (
											<>
												{hasExtension || hasValidValue("surface")
													? " pour un montant prévisionnel total de "
													: "Le montant prévisionnel total est de "}{" "}
												{taxMode === "HT" && (
													<b>{formatCurrencyPrecise(totalAmountHT)} HT</b>
												)}
												{taxMode === "TTC" && (
													<b>{formatCurrencyPrecise(totalAmountTTC)} TTC</b>
												)}
											</>
										)}
										.
									</p>
								)}
							</>
						)}

					{startDate && deliveryDate && (
						<p>
							Les travaux devraient être réalisés entre le{" "}
							<b>{format(startDate, "dd/MM/yyyy")}</b> et le{" "}
							<b>{format(deliveryDate, "dd/MM/yyyy")}</b>.
						</p>
					)}

					{cityHallStartDate && (
						<p>
							Une déclaration d'ouverture de chantier a été déposée en mairie
							pour un démarrage au{" "}
							<b>{format(cityHallStartDate, "dd/MM/yyyy")}</b>.
						</p>
					)}
				</div>
				<div className="flex flex-col gap-4">
					<h1 className="text-[#144C65] text-md font-medium">
						Risques associés
					</h1>
					<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4 bg-gray-100">
						<div className="flex flex-row items-center space-x-2 justify-between">
							<p>Risque de gonflement et retrait des argiles (RGA)</p>
							<RiskBadge riskCode={rga} />
						</div>
						<div className="flex flex-row items-center space-x-2 justify-between">
							<p>Sismique</p>
							<RiskBadge riskCode={sismic} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const RiskBadge = ({ riskCode }: { riskCode?: number }) => {
	switch (riskCode) {
		case 0:
			return (
				<div className="rounded-2xl px-2 py-1 bg-blue-600 text-white">
					Très Faible
				</div>
			);
		case 1:
			return (
				<div className="rounded-2xl px-2 py-1 bg-green-600 text-white">
					Faible
				</div>
			);
		case 2:
			return (
				<div className="rounded-2xl px-2 py-1 bg-orange-600 text-white">
					Modéré
				</div>
			);
		case 3:
			return (
				<div className="rounded-2xl px-2 py-1 bg-red-600 text-white">Elevé</div>
			);
		default:
			return (
				<div className="rounded-2xl px-2 py-1 bg-gray-400 text-white">Non</div>
			);
	}
};

export default DetailsDescriptionContainer;
